import "bootstrap/dist/css/bootstrap.min.css";
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

// Layouts
import { MainLayout } from "./components/layouts/Containers/MainContainer.js";

// SessionManager
import SessionManager from "./components/auth/SessionManager.js";

// Year
// import CreateYearScreen from "./screens/Year/CreateYearScreen";
import TutorialCreateYearScreen from "./screens/Year/TutorialCreateYearScreen";
import YearScreen from "./screens/Year/YearScreen";

// Day
import DayScreen from "./screens/Day/DayScreen";

// Week
import WeekScreen from "./screens/Wek/WeekScreen.js";

// Landing
import LandingScreen from "./screens/LandingPage/LandingScreen";

// Insight
import TestInsightScreen from "./screens/Insight/TestInsightScreen";

// Login / Register
import ViewLoginScreen from "./screens/Login/ViewLoginScreen";
import ViewRegisterScreen from "./screens/Register/ViewRegisterScreen";
import AboutYouScreen from "./screens/About/AboutYouScreen";

// My Account
import ProfileScreen from "./screens/Profile/ProfileScreen";
import ProfileMenuScreen from "./screens/Profile/ProfileMenu";
import ProfileFAQScreen from "./screens/Profile/ProfileFAQScreen";
import { ProfileLogScreen } from "./screens/Profile/ProfileLogScreen.js";
import ProfileDataScreen from "./screens/Profile/ProfileDataScreen.js";
import ProfilePreferencesScreen from "./screens/Profile/ProfilePreferencesScreen.js";

// Blog
import BlogScreen from "./screens/Blog/BlogScreen";
import BlogPostTest from "./screens/Blog/BlogPostTest";
import BlogSearchScreen from "./screens/Blog/BlogSearchScreen";

//Footer
import AboutScreen from "./screens/About/AboutScreen";
import MissionScreen from "./screens/About/MissionScreen";
import ContactScreen from "./screens/Contact/ContactScreen";
import PrivacyScreen from "./screens/PrivacyPolicy/PrivacyPolicy";
import TermsConditionsScreen from "./screens/PrivacyPolicy/TermsConditions";

//External Packages
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

//Styles
import "./App.css";
import CommonsExplainerScreen from "./screens/About/CommonsExplainerScreen.js";
import DonateScreen, { DonationForm } from "./screens/Donate/DonateScreen.js";
import DonateSuccess from "./screens/Donate/components/DonateSuccess.js";
import RouteChangeTracker from "./components/RouteChangeTracker.js";
import { TutorialGallery } from "./screens/Tutorial/TutorialGallery.js";
import ModuleMenu from "./screens/ArtofLooking/components/ModuleMenu/ModuleMenu.js";
import LearningPlayer from "./screens/ArtofLooking/components/LearningPlayer/LearningPlayer.js";
import FinalQuizHost from "./screens/ArtofLooking/components/QuizHost/FinalQuizHost.js";
import ValuesScreen from "./screens/Values/ValuesScreen.js";
import { logout } from "./actions/userActions.js";

const API_URL = process.env.REACT_APP_API_URL;

// Checks on mount whether the stored session is still valid.
// Must render inside <Router> so useNavigate is available.
function SessionChecker() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.userLogin);

  useEffect(() => {
    if (!userInfo) return;
    axios
      .get(`${API_URL}/api/users/profile`, { withCredentials: true })
      .catch((err) => {
        if (err.response?.status === 401) {
          dispatch(logout());
          navigate("/login");
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function App() {
  const [stripePromise, setStripePromise] = useState(null);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  useEffect(() => {
    fetch("https://api.nowasteddays.io/config")
      .then(async (r) => {
        if (!r.ok) {
          throw new Error("Failed to fetch config");
        }

        const { publishableKey } = await r.json();
        setStripePromise(loadStripe(publishableKey));
      })
      .catch((error) => {
        console.log("Error:", error);
      });
  }, []);

  const modalTracker = () => {
    const href = window.location.href;
    if (
      href.endsWith("/") ||
      href.endsWith("/login") ||
      href.endsWith("/register") ||
      href.includes("/blog")
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <Router>
      <RouteChangeTracker />
      <SessionChecker />
      <SessionManager modal={modalTracker()}>
      <Routes>
        <Route path="/" element={<LandingScreen />} />
        <Route path="/login" element={<ViewLoginScreen />} />
        <Route path="/register" element={<ViewRegisterScreen />} />
        <Route path="/about-nowasteddays" element={<AboutScreen />} />
        <Route
          path="/digital-commons-project"
          element={<CommonsExplainerScreen />}
        />
        <Route path="/our-mission" element={<MissionScreen />} />

        <Route path="/nwd-commons" element={<ViewRegisterScreen />} />
        <Route
          path="/nowasteddays-commons"
          element={<CommonsExplainerScreen />}
        />
        <Route path="/about-you" element={<AboutYouScreen />} />
        <Route path="/insights" element={<TestInsightScreen />} />
        <Route path="/privacy-policy" element={<PrivacyScreen />} />
        <Route
          path="/terms-and-conditions"
          element={<TermsConditionsScreen />}
        />
        <Route
          path="/donate"
          element={<DonateScreen stripePromise={stripePromise} />}
        />
        <Route
          path="/donate-success"
          element={<DonateScreen stripePromise={stripePromise} />}
        />

        <Route path="/blog" element={<BlogScreen />} />
        <Route path="/blog/:slug" element={<BlogPostTest />} />
        <Route path="/blog/search/:term" element={<BlogSearchScreen />} />

        <Route path="/my-account/contact" element={<ContactScreen />} />
        <Route path="/my-account/security" element={<ProfileScreen />} />
        <Route path="/my-account/menu" element={<ProfileMenuScreen />} />

        <Route path="/my-account/journal" element={<ProfileLogScreen />} />
        <Route path="/my-account/my-data" element={<ProfileDataScreen />} />
        <Route
          path="/my-account/preferences"
          element={<ProfilePreferencesScreen />}
        />
        <Route path="/week/create" element={<WeekScreen />} />
        <Route path="/week/:id" element={<WeekScreen />} />
        <Route path="/contact" element={<ContactScreen />} />
        <Route path="/contact/:type" element={<ContactScreen />} />
        <Route path="/year/create" element={<YearScreen />} />
        <Route path="/year/:id" element={<YearScreen />} />
        <Route path="/today" element={<DayScreen />} />
        <Route path="/today/:id" element={<DayScreen />} />
        <Route path="/my-values" element={<ValuesScreen />} />

        <Route element={<MainLayout />}>
          <Route path="/my-account/faqs" element={<ProfileFAQScreen />} />
          <Route path="/tutorial" element={<TutorialGallery />} />
          <Route path="/art-of-looking-menu" element={<ModuleMenu />} />
          <Route path="/art-of-looking/:slug" element={<LearningPlayer />} />
          <Route
            path="/art-of-looking/final-quiz"
            element={<FinalQuizHost />}
          />
        </Route>
      </Routes>
      </SessionManager>
    </Router>
  );
}
export default App;
